"use client";

import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Section, stagger, item } from "./Section";
import tech from "@/assets/program-tech.jpg";
import arts from "@/assets/program-arts.jpg";
import sports from "@/assets/program-sports.jpg";
import science from "@/assets/program-science.jpg";
import library from "@/assets/program-library.jpg";
import Image from "next/image";

type Card = {
  title: string;
  tag: string;
  copy: string;
  img: string;
  className: string;
  dark?: boolean;
};

const cards: Card[] = [
  {
    title: "Technology & Engineering",
    tag: "01 — STEM",
    copy: "From robotics to AI labs, our makerspaces turn curiosity into invention.",
    img: tech,
    className: "md:col-span-3 md:row-span-2",
    dark: true,
  },
  {
    title: "Visual & Performing Arts",
    tag: "02 — Studio",
    copy: "Five studios. Two stages. Endless self-expression.",
    img: arts,
    className: "md:col-span-3 md:row-span-1",
    dark: true,
  },
  {
    title: "Athletics",
    tag: "03 — Field",
    copy: "16 varsity teams. Discipline meets joy.",
    img: sports,
    className: "md:col-span-2 md:row-span-1",
    dark: true,
  },
  {
    title: "Sciences",
    tag: "04 — Lab",
    copy: "Wet labs, field studies, and university-grade equipment.",
    img: science,
    className: "md:col-span-2 md:row-span-2",
    dark: true,
  },
  {
    title: "Humanities Library",
    tag: "05 — Study",
    copy: "60,000 volumes. A second home for thinkers.",
    img: library,
    className: "md:col-span-2 md:row-span-1",
    dark: true,
  },
];

export function Programs() {
  return (
    <Section id="programs" className="bg-cream">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.25em] text-gold">
              Our Programs
            </p>
            <h2 className="mt-3 text-balance text-4xl font-extrabold text-navy md:text-6xl">
              Five disciplines.{" "}
              <span className="font-serif-body italic font-medium text-navy/70">
                One curious mind.
              </span>
            </h2>
          </div>
          <p className="max-w-md font-serif-body text-lg text-muted-foreground">
            A curriculum built around how students actually learn — through
            making, testing, performing and questioning.
          </p>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid auto-rows-[200px] grid-cols-1 gap-4 md:auto-rows-[220px] md:grid-cols-6"
        >
          {cards.map((c) => (
            <motion.a
              key={c.title}
              href="#enroll"
              variants={item}
              whileHover={{ scale: 1.015 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className={`group relative overflow-hidden rounded-3xl ${c.className}`}
            >
              <Image
                src={c.img}
                alt={c.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-navy/40 to-transparent" />

              <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-7 text-primary-foreground">
                <div className="flex items-center justify-between">
                  <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                    {c.tag}
                  </span>
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-white/10 backdrop-blur transition-all group-hover:bg-gold group-hover:text-navy-deep">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-xl font-extrabold leading-tight md:text-2xl">
                    {c.title}
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-white/80">{c.copy}</p>
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}
